<template>
    <v-row
        class="FontSelector FontSelector"
        dense
    >
        <v-col
            cols="12"
            class="pa-0 mb-4"
        >
            <v-row dense>
                <v-col
                    cols="12"
                    sm="5"
                >
                    <div class="FontSelector__input">
                        <v-autocomplete
                            :items="fonts"
                            item-text="family"
                            item-value="family"
                            label="Font Family"
                            v-model="family"
                            @change="onFamilyChange()"
                            outlined
                            dense
                        />
                    </div>
                </v-col>
                <v-col
                    cols="12"
                    sm="5"
                >
                    <div class="FontSelector__input">
                        <v-autocomplete
                            :items="fontVariants"
                            item-text="value"
                            item-value="value"
                            label="Variant"
                            :disabled="!family"
                            v-model="variant"
                            @change="setStoreData()"
                            outlined
                            dense
                        />
                    </div>
                </v-col>
                <v-col
                    cols="12"
                    sm="2"
                >
                    <div class="FontSelector__input FontSelector__input--checkbox">
                        <v-checkbox
                            label="Uppercase"
                            :disabled="!family"
                            v-model="allCaps"
                            dense
                            @change="setStoreData()"
                            type="number"
                        />
                    </div>
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12">
                    <div class="FontSelector__input">
                        <h4 class="text-subtitle-2">
                            Site Preview
                        </h4>
                    </div>
                </v-col>
                <v-col
                    cols="12"
                    sm="4"
                >
                    <div class="FontSelector__input">
                        <v-text-field
                            label="Size"
                            :disabled="!family"
                            v-model="previewSize"
                            outlined
                            dense
                            @change="setStoreData()"
                            type="number"
                            suffix="px"
                        />
                    </div>
                </v-col>

                <v-col
                    cols="12"
                    sm="4"
                >
                    <div class="FontSelector__input">
                        <v-text-field
                            label="Letter Spacing"
                            :disabled="!family"
                            v-model="previewLetterSpacing"
                            outlined
                            dense
                            @change="setStoreData()"
                            type="number"
                            suffix="px"
                        />
                    </div>
                </v-col>

                <v-col
                    cols="12"
                    sm="4"
                >
                    <div class="FontSelector__input">
                        <v-text-field
                            label="Line Height"
                            :disabled="!family"
                            v-model="previewLineHeight"
                            outlined
                            dense
                            @change="setStoreData()"
                            type="number"
                        />
                    </div>
                </v-col>
            </v-row>

            <v-row
                dense
                v-if="font.key === 'body' || font.key === 'h1'"
            >
                <v-col cols="12">
                    <div class="FontSelector__input">
                        <h4 class="text-subtitle-2">
                            Font Selector
                        </h4>
                    </div>
                </v-col>
                <v-col
                    cols="12"
                    sm="4"
                >
                    <div class="FontSelector__input">
                        <v-text-field
                            label="Size"
                            :disabled="!family"
                            v-model="selectorSize"
                            @change="setStoreData()"
                            outlined
                            dense
                            type="number"
                            suffix="px"
                        />
                    </div>
                </v-col>

                <v-col
                    cols="12"
                    sm="4"
                >
                    <div class="FontSelector__input">
                        <v-text-field
                            label="Letter Spacing"
                            :disabled="!family"
                            v-model="selectorLetterSpacing"
                            @change="setStoreData()"
                            outlined
                            dense
                            type="number"
                            suffix="px"
                        />
                    </div>
                </v-col>

                <v-col
                    cols="12"
                    sm="4"
                >
                    <div class="FontSelector__input">
                        <v-text-field
                            label="Line Height"
                            :disabled="!family"
                            v-model="selectorLineHeight"
                            @change="setStoreData()"
                            outlined
                            dense
                            type="number"
                        />
                    </div>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import { mapActions } from 'vuex';
import { EventBus } from '@/store/eventBus';

export default {
    props: {
        id: {
            type: String,
            required: true
        },

        fonts: {
            type: Array,
            required: true
        },

        font: {
            type: Object,
        }
    },

    data() {
        return {
            allCaps: false,
            family: undefined,
            fontPreviewStyle: undefined,
            fontVariants: [],
            previewSize: 16,
            previewLineHeight: 1.4,
            previewLetterSpacing: 0,
            selectorSize: 16,
            selectorLineHeight: 1.4,
            selectorLetterSpacing: 0,
            variant: undefined,
        };
    },

    mounted() {
        this.setFontData();
    },

    methods: {
        ...mapActions({
            setSinglePair: 'fontPairingEditor/setSinglePair',
        }),

        onFamilyChange() {
            const currentVariant = this.variant;
            this.loadVariants()
                .then(() => {
                    // If the current variant exists in the new font, nothing has to change.
                    if (!this.fontVariants.includes(currentVariant)) {
                        this.variant = 'regular';
                    }

                    this.setStoreData();
                });
        },

        setFontData() {
            this.family = this.font.family;

            this.loadVariants()
                .then(() => {
                    this.variant = this.font.variant;
                    this.allCaps = this.font.all_caps;
                    this.previewSize = this.font.preview_size;
                    this.previewLineHeight = this.font.preview_line_height;
                    this.previewLetterSpacing = this.font.preview_letter_spacing;
                    this.selectorSize = this.font.selector_size;
                    this.selectorLineHeight = this.font.selector_line_height;
                    this.selectorLetterSpacing = this.font.selector_letter_spacing;
                    this.setStoreData();
                });
        },

        loadVariants() {
            return new Promise((resolve, reject) => {
                const fontVariants = this.fonts.filter(font => {
                    return font.family === this.family;
                });

                if (fontVariants) {
                    this.fontVariants = fontVariants[0].variants;
                    resolve();
                    return;
                }

                reject('Could not set variants');
            });
        },

        setStoreData() {
            const pair = {
                'id': this.id,
                'family': this.family,
                'variant': this.variant,
                'all_caps': this.allCaps,
                'preview_size': this.previewSize,
                'preview_line_height': this.previewLineHeight,
                'preview_letter_spacing': this.previewLetterSpacing,
                'selector_size': this.selectorSize,
                'selector_line_height': this.selectorLineHeight,
                'selector_letter_spacing': this.selectorLetterSpacing,
            };

            this.setSinglePair(pair).then(() => {
                EventBus.$emit('FontEditPreviewChanged', true);
            });
        }
    }
};
</script>
<style lang="scss">
.FontSelector {
    border-bottom: 1px solid #cdcdcd;

    &:last-of-type {
        border-bottom: 0;
        margin-bottom: 0;
    }
}

.FontSelector__input--checkbox {
    text-align: center;

    .v-input--selection-controls {
        margin-top: 3px !important;
    }
}
</style>
